import { Toaster as SonnerToaster } from "@src/lib/sonner";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "../Icon";

const ICON_SIZE = 20;

export function getSuccessIcon(color: string) {
  return <Icon name="checkmark" size={ICON_SIZE} color={color} />;
}

export function getErrorIcon(color: string) {
  return <Icon name="warning" size={ICON_SIZE} color={color} />;
}

export function getWarningIcon(color: string) {
  return <Icon name="emergency" size={ICON_SIZE} color={color} />;
}

export function getInfoIcon(color: string) {
  return <Icon name="info" size={ICON_SIZE} color={color} />;
}

/**
 * Toaster component that wraps sonner-native's Toaster with app-specific defaults.
 * Place this component at the root of your app (in _layout.tsx).
 */
export function Toaster() {
  const { theme } = useUnistyles();

  // Create icons with theme color - must be ReactNode, not functions
  const icons = {
    success: getSuccessIcon(theme.colors.statusBadge.liveBorder),
    error: getErrorIcon(theme.colors.status.error),
    warning: getWarningIcon(theme.colors.warning[500]),
    info: getInfoIcon(theme.colors.interactive.buttons.default_background),
  };

  return (
    <SonnerToaster
      position="top-center"
      offset={60}
      duration={4000}
      style={styles.toast}
      icons={icons}
      visibleToasts={3}
      theme={theme.currentTheme}
      toastOptions={{
        // @ts-ignore baseline 2026-02-16
        toastContentStyle: styles.toastContent,
        actionButtonStyle: { ...styles.button, ...styles.actionButton },
        actionButtonTextStyle: { ...styles.buttonText, ...styles.actionButtonText },
        cancelButtonStyle: { ...styles.button, ...styles.cancelButton },
        cancelButtonTextStyle: { ...styles.buttonText, ...styles.cancelButtonText },
        buttonsStyle: styles.buttons,
        descriptionStyle: styles.description,
        titleStyle: styles.title,
        toastContainerStyle: styles.toastContainer,
        closeButtonStyle: styles.closeButton,
      }}
    />
  );
}

const styles = StyleSheet.create((theme) => ({
  toastContainer: {
    // Add some styles for the toast container if needed
  },
  toast: {
    borderRadius: 8,
    backgroundColor: theme.colors.surface1.background,
    borderColor: theme.colors.border,
    borderWidth: 1,
  },
  toastContent: {
    gap: theme.spacing.sm,
    alignItems: "flex-start",
  },
  title: {
    // Add some styles for the title text if needed
  },
  description: {
    color: theme.colors.textMuted,
    lineHeight: 20,
    fontSize: 14,
  },
  buttons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xxs,
  },
  button: {
    backgroundColor: "blue",
    height: 42,
    minWidth: 80,
    borderRadius: 8,
    borderCurve: "continuous",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: 0,
  },
  actionButton: {
    backgroundColor: theme.colors.interactive.buttons.default_background,
  },
  cancelButton: {
    backgroundColor: theme.colors.interactive.secondary.default_background,
    borderColor: theme.colors.border,
    borderWidth: 1.5,
  },
  buttonText: {
    fontWeight: "regular",
    fontSize: 14,
    width: "100%",
    textAlign: "center",
  },
  actionButtonText: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
  cancelButtonText: {
    color: theme.colors.text,
  },
  closeButton: {
    opacity: 0.6,
    position: "absolute",
    top: 0,
    right: 0,
  },
}));
